import { useState } from "react";
import { BUCKETS, BUCKET_LABELS } from "../../api";
import type { Bucket, Int64 } from "../../api";
import { Button, cx } from "../../components";
import { useCountUp, useSectionReveal } from "../../motion";
import { bucketStyle } from "../../theme";
import {
  MISSION,
  PACK,
  SCENARIO_INITIAL,
  TROIS_JOURS,
  acheter,
  cloturer,
  gestesPossibles,
  imputer,
  reserver,
} from "./GrandLivreScenario";
import type { Scenario } from "./GrandLivreScenario";
import styles from "./Vitrine.module.css";

const formatEntier = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
const formatTokensEntiers = (v: Int64) => formatEntier.format(BigInt(v));

function Compteur({ value, className }: { value: Int64; className?: string }) {
  const ref = useCountUp<HTMLSpanElement>(value, { format: formatTokensEntiers });
  return <span ref={ref} className={cx("lde-tabular", className)} />;
}

/**
 * L'invariant du grand livre, à essayer : quatre seaux, une égalité, des
 * boutons qui font les gestes du produit. Chaque chiffre monte vers sa
 * nouvelle valeur et la somme ne bouge que quand on achète.
 */
export function VitrineInvariant() {
  const section = useSectionReveal<HTMLElement>();
  const [scenario, setScenario] = useState<Scenario>(SCENARIO_INITIAL);
  const possibles = gestesPossibles(scenario);

  const seaux: Record<Bucket, Int64> = {
    available: scenario.available,
    reserved: scenario.reserved,
    consumed: scenario.consumed,
    expired: scenario.expired,
  };

  return (
    <section ref={section} id="grand-livre" className={cx(styles.section, styles.ecran)} aria-labelledby="livre-titre">
      <div className={styles.sectionEntete} data-anim="reveal">
        <p className={styles.surtitre}>Le grand livre</p>
        <h2 id="livre-titre" className={styles.sectionTitre}>
          Un grand livre qui ne se corrige pas : il s'ajoute.
        </h2>
        <p className={styles.sectionChapo}>
          Aucune écriture n'est modifiée ni supprimée. Quatre seaux, et une égalité vraie à tout instant.
          Essayez.
        </p>
      </div>

      <div className={styles.equation} data-anim="reveal" aria-live="polite">
        {BUCKETS.map((b, i) => (
          <div key={b} className={styles.equationTerme}>
            {i > 0 && (
              <span className={styles.equationSigne} aria-hidden="true">
                +
              </span>
            )}
            <div className={styles.tuile} style={bucketStyle(b)}>
              <Compteur value={seaux[b]} className={styles.tuileValeur} />
              <span className={styles.tuileNom}>{BUCKET_LABELS[b]}</span>
            </div>
          </div>
        ))}
        <div className={styles.equationTerme}>
          <span className={styles.equationSigne} aria-hidden="true">
            =
          </span>
          <div className={cx(styles.tuile, styles.tuileTotal)}>
            <Compteur value={scenario.entered} className={styles.tuileValeur} />
            <span className={styles.tuileNom}>Total entré</span>
          </div>
        </div>
      </div>

      <div className={styles.gestes} data-anim="reveal">
        <Button onClick={() => setScenario((s) => acheter(s, PACK))}>Acheter un pack de {formatTokensEntiers(PACK)}</Button>
        <Button onClick={() => setScenario((s) => reserver(s, MISSION))} disabled={!possibles.reserver}>
          Réserver un projet de {formatTokensEntiers(MISSION)}
        </Button>
        <Button onClick={() => setScenario((s) => imputer(s, TROIS_JOURS))} disabled={!possibles.imputer}>
          Imputer 3 jours ({formatTokensEntiers(TROIS_JOURS)})
        </Button>
        <Button onClick={() => setScenario(cloturer)} disabled={!possibles.cloturer}>
          Clôturer le projet
        </Button>
        <Button variant="ghost" onClick={() => setScenario(SCENARIO_INITIAL)}>
          Réinitialiser
        </Button>
      </div>

      <p className={styles.note} data-anim="reveal">
        Le serveur revérifie cette égalité à chaque lecture du portefeuille et signale toute anomalie. Un
        seul taux € → token par achat, figé pour toute la vie du pack.
      </p>
    </section>
  );
}
